/* eslint-disable no-nested-ternary */
import React from 'react';
import {
  Button,
  colors,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeading,
  TableRow,
} from '@insurancemenu/imenu-components-lib';
import clsx from 'clsx';
import { useRootStore } from 'store/rootStore';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { createStyles as createModalStyles } from 'components/common/modals/ActionModal/styles';
import { Status, EmailMapping } from 'types/emailService';
import { capitalize } from '@insurancemenu/imenu-components-lib/dist/helpers';
import { EmailReceiver, EnrollmentStage } from 'types/enrollmentService';
import { UserType } from 'types/general';
import { WarningFilled } from '@insurancemenu/imenu-components-lib/dist/icons';
import { createStyles } from './styles';
import { EMAIL_STATUS_ICON } from '../../TriggerRenewalsTable/TriggerRenewalsRow';

interface EmailsModalProps {
  show: boolean;
}

const EmailsModal = observer(({ show }: EmailsModalProps): JSX.Element => {
  const classes = createStyles();
  const modalStyles = createModalStyles(true); // common modal styles

  const {
    triggerRenewalsStore: {
      loading,
      currentGroup,
      emailsByPeriodId,
      currentPeriod,
      missingExhibits,
      missingUsersEmailsMessage,
      closeEmailsModal,
      triggerRenewal,
      sendEmailToSingleUser,
      getUserNameByEmail,
    },
  } = useRootStore();

  const convertedEmails = emailsByPeriodId.map((email) => toJS(email)).slice();
  const convertedGroup = toJS(currentGroup);
  const convertedPeriod = toJS(currentPeriod);

  const emailReceivers: EmailReceiver[] = convertedEmails.map(({ emailAddress, userType }) => {
    return { email: emailAddress, userType };
  });

  const sendEmail = async (emailReceiver: EmailReceiver): Promise<void> => {
    await sendEmailToSingleUser(convertedPeriod, emailReceiver);
  };

  const notifyAll = (): void => {
    void triggerRenewal(convertedPeriod, emailReceivers);
  };

  const moveToRenewal = (): void => {
    void triggerRenewal(convertedPeriod, emailReceivers, false);
  };

  const sortByType = (emails: EmailMapping[]): EmailMapping[] => {
    return emails.sort((a, b) =>
      a.userType === b.userType
        ? 0
        : a.userType === UserType.AOR
        ? -1
        : b.userType === UserType.AOR
        ? 1
        : a.userType === UserType.ADMIN
        ? -1
        : 1,
    );
  };

  const emailsRows = sortByType(convertedEmails).map(({ emailId, emailAddress, status, userType }) => (
    <TableRow className={classes.tableRow} key={`${userType}_${emailAddress}`}>
      <TableCell className={classes.tableCell}>{UserType[userType]}</TableCell>
      <TableCell className={classes.tableCell}>{getUserNameByEmail(emailAddress, userType)}</TableCell>
      <TableCell className={classes.tableCell}>{emailAddress}</TableCell>
      <TableCell className={classes.tableCell}>
        <p className={classes.email} key={emailId}>
          <span className={classes[`status_${status}`]}>
            {capitalize(Status[status].replaceAll('_', ' ').toLowerCase())}
          </span>
          {EMAIL_STATUS_ICON[status]}
        </p>
      </TableCell>
      <TableCell className={classes.tableCell}>
        <Button
          type="text"
          size="medium"
          color="primary"
          onClick={(): void => {
            void sendEmail({ email: emailAddress, userType });
          }}
          className={classes.textBtn}
        >
          Send Email
        </Button>
      </TableCell>
    </TableRow>
  ));

  const ErrorBlock = (color: string, errorMessage: string): JSX.Element => (
    <div className={clsx(modalStyles.contentText, classes.warningWrapper)}>
      <WarningFilled width="2.4rem" color={color} />
      <span className={classes.missingExhibits}>{errorMessage}</span>
    </div>
  );

  return (
    <Modal show={show} onClose={closeEmailsModal} className={clsx(modalStyles.modal, classes.modal)}>
      <h3 className={clsx(modalStyles.modalTitle, classes.modalTitle)}>Send Emails</h3>
      <p className={clsx(modalStyles.contentText, classes.groupName)}>{convertedGroup.name}</p>
      {(missingExhibits || missingUsersEmailsMessage) && (
        <div className={classes.warningsBlock}>
          {missingExhibits && ErrorBlock(colors.red[600], missingExhibits)}
          {missingUsersEmailsMessage && ErrorBlock(colors.yellow[600], missingUsersEmailsMessage)}
        </div>
      )}
      <footer className={classes.modalFooter}>
        <div className={classes.tableWrapper}>
          <Table loading={loading} totalElements={convertedEmails.length}>
            <TableHeader className={classes.tableHeader}>
              <TableRow>
                <TableHeading className={classes.tableHeading}>Type</TableHeading>
                <TableHeading className={classes.tableHeading}>Name</TableHeading>
                <TableHeading className={classes.tableHeading}>Email Address</TableHeading>
                <TableHeading className={classes.tableHeading}>Status</TableHeading>
                <TableHeading className={classes.tableHeading} />
              </TableRow>
            </TableHeader>
            <TableBody>{emailsRows}</TableBody>
          </Table>
        </div>
        <div className={classes.actionsBtnWrapper}>
          {convertedPeriod.stage === EnrollmentStage.INSTALLED && (
            <Button color="primary" type="outlined" onClick={moveToRenewal} size="large">
              Move to Renewal
            </Button>
          )}
          <Button color="primary" onClick={notifyAll} size="large">
            Notify all Users
          </Button>
        </div>
      </footer>
    </Modal>
  );
});

export default EmailsModal;
